import {AppThunkAction} from "src/~store/models/AppThunkAction";

import {
    GetAllChannelsAction, 
    CreateChannelAction, 
    SaveChannelAction,
    DeleteChannelAction,
    GetAllOperatorsAction,
    SaveChannelSettingAction,
    ToggleChannelSortingAction,
    RestartChannelAction,
    GetQrCodeAction,
    ImportChannelOperatorsAction
} from "./interfaces";

import {
    GET_ALL_CHANNELS_START, 
    GET_ALL_CHANNELS_SUCCESS, 
    GET_ALL_CHANNELS_ERROR, 
    CREATE_CHANNEL,
    CANCEL_CREATE_CHANNEL,
    DELETE_CHANNEL_START,
    DELETE_CHANNEL_SUCCESS,
    GET_ALL_OPERATORS_START,
    GET_ALL_OPERATORS_SUCCESS,
    GET_ALL_OPERATORS_ERROR,
    SAVE_CHANNEL_SETTING,
    DELETE_NEW_CHANNEL_SUCCESS,
    SAVE_NEW_CHANNEL_SUCCESS,
    TOGGLE_CHANNEL_SORTING_ACTION,
    RESTART_CHANNEL_START,
    RESTART_CHANNEL_SUCCESS,
    GET_QR_CODE_START,
    GET_QR_CODE_SUCCESS,
    GET_QR_CODE_ERROR,
    IMPORT_CHANNEL_OPERATORS_SUCCESS
} from "./types";

import {
    getAllChannelsAsync,
    saveChannelAsync,
    deleteChannelAsync,
    saveChannelSettingAsync,
    getSettingByChannelIdAsync,
    getSettingsByChannelsIdAsync,
    restartChannelAsync,
    getChannelContactsCountByChannelIdAsync,
    getTelegramQrCodeAsync,
    importChannelOperatorsAsync
} from "../../~api/actions";

import {IChannel} from "../models/Channel";

import {
    showSuccessToastr, 
    showErrorToastr 
} from "src/app/shared/helpers/toastr-helper/ToastrHelper";

import { initNewSetting } from "src/app/setting/~store/state/InitSettingsState";
import { CHANNEL_SETTING_PREFIX } from "../../channel-main/channel-list/channel-item/ChannelItemConstants";
import i18n from "src/app/shared/localization/i18n";
import { getAllOperatorsAsync } from "src/app/operator/~api/actions";

// channel list

export const getAllChannels = (): AppThunkAction<GetAllChannelsAction> => async dispatch => {
    dispatch({type: GET_ALL_CHANNELS_START});
    const result = await getAllChannelsAsync();
    if (result.isSuccess) {
        const channels = result.value ?? [];

        const settingsResult = await getSettingsByChannelsIdAsync(channels.map((item) => item.channelId));
        if(settingsResult.isSuccess) {
            channels.forEach(channel => channel.setting = settingsResult.value?.find(s => s.id === CHANNEL_SETTING_PREFIX + channel.channelId) || initNewSetting());
            dispatch({type: GET_ALL_CHANNELS_SUCCESS, payload: {channels}});
            return;
        }  
    }
    dispatch({type: GET_ALL_CHANNELS_ERROR});
};

export const getAllOperators = (): AppThunkAction<GetAllOperatorsAction> => async dispatch => {
    dispatch({type: GET_ALL_OPERATORS_START});

    const result = await getAllOperatorsAsync();

    if(result.isSuccess) {
        const operators = result.value ? result.value : [];
        dispatch({type: GET_ALL_OPERATORS_SUCCESS, payload: {operators}})
    } else {
        dispatch({type: GET_ALL_OPERATORS_ERROR})
    }
}

export const createChannel = (): AppThunkAction<CreateChannelAction> => async dispatch => {
    dispatch({type: CREATE_CHANNEL});
};

export const cancelCreateChannel = (): AppThunkAction<CreateChannelAction> => async dispatch => {
    dispatch({type: CANCEL_CREATE_CHANNEL});
};

export const saveChannel = (channel: IChannel): AppThunkAction<SaveChannelAction> => async (dispatch, getState) => {
    if(getState().channelState.channelList.isSavingChannel) return;

    const result = await saveChannelAsync(channel);
    if(result.isSuccess) {
        if(channel.channelId === undefined) {
            showSuccessToastr(i18n.t('channels.saveNewChannelSuccessMessage'));
            dispatch({ type: SAVE_NEW_CHANNEL_SUCCESS })
        } else {
            showSuccessToastr(i18n.t('channels.saveChannelSuccessMessage'));
        }
    } else {
        result.tryShowErrorToastr();
    }

    getAllChannels()(dispatch, getState);
}

export const deleteChannel = (channel: IChannel): AppThunkAction<DeleteChannelAction> => async (dispatch, getState) => {
    dispatch({type: DELETE_CHANNEL_START});

    if(channel.channelId === 0) {
        showSuccessToastr(i18n.t('channels.deleteNewChannelSuccessMessage'));
        dispatch({type: DELETE_NEW_CHANNEL_SUCCESS});
        dispatch({type: DELETE_CHANNEL_SUCCESS});
    } else {
        const channelContactsCount = await getChannelContactsCountByChannelIdAsync(channel.channelId)
        if (channelContactsCount?.value === 0 || 
            window.confirm(i18n.t('channels.askYouWannaDeleteChannel').replace('{0}', (channelContactsCount?.value ?? "") as string))) {
            const result = await deleteChannelAsync(channel);

            if (result.isSuccess) {
                showSuccessToastr(i18n.t('channels.deleteChannelSuccessMessage'));
            }
            else {
                const errorText = JSON.parse(result.errorText ?? "null");
                if (errorText?.errorCode === "error.deleteWithOpenChats") {
                    result.tryShowErrorToastr();
                }
                else {
                    showErrorToastr(i18n.t('channels.deleteChannelErrorMessage'));
                }
            }
        }
        else {
            return;
        }
    }

    getAllChannels()(dispatch, getState);
}

export const restartChannel = (channel: IChannel): AppThunkAction<RestartChannelAction> => async (dispatch, getState) => {
    dispatch({ type: RESTART_CHANNEL_START });

    const result = await restartChannelAsync(channel);

    if (result.isSuccess) {
        await dispatch({ type: RESTART_CHANNEL_SUCCESS });
        showSuccessToastr(i18n.t('channels.restartChannelSuccessMesage'));
    }

    getAllChannels()(dispatch, getState);
}

export const saveChannelSetting = (
    channel: IChannel, 
    setting: string
): AppThunkAction<SaveChannelSettingAction> => async (dispatch, getState) => {
    const result = await saveChannelSettingAsync(channel.channelId as number, setting);

    if(result.isSuccess) {
        dispatch({ type: SAVE_CHANNEL_SETTING });
        showSuccessToastr(i18n.t('settings.settingSaved'));
    }
    else {
        result.tryShowErrorToastr();
    }

    getAllChannels()(dispatch, getState);
}

export const toggleChannelSorting = () : AppThunkAction<ToggleChannelSortingAction> => async dispatch => {
    dispatch({type: TOGGLE_CHANNEL_SORTING_ACTION});
}

export const fetchQrCode = (channelId: number): AppThunkAction<GetQrCodeAction> => async (dispatch) => {
    dispatch({ type: GET_QR_CODE_START });

    const result = await getTelegramQrCodeAsync(channelId);

    if (result.isSuccess && result.value) {
        dispatch({
            type: GET_QR_CODE_SUCCESS,
            payload: result.value 
        });
    } else {
        dispatch({ type: GET_QR_CODE_ERROR });
        if (result.tryShowErrorToastr) {
            result.tryShowErrorToastr();
        } else {
            showErrorToastr(i18n.t('error.fetchFailedQr'));
        }
    }
};

export const importChannelOperators = (sourceChannelId: number, targetChannelId: number, keepSourceOperators: boolean): AppThunkAction<ImportChannelOperatorsAction> =>
    async (dispatch, getState) => {
        const result = await importChannelOperatorsAsync(sourceChannelId, targetChannelId, keepSourceOperators);

        if (result.isSuccess) {
            dispatch({ type: IMPORT_CHANNEL_OPERATORS_SUCCESS});
            showSuccessToastr(i18n.t("channels.operatorsImported"));
        } else {
            result.tryShowErrorToastr();
        }

        getAllChannels()(dispatch, getState);
    };